/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { CSSProperties, ReactNode } from 'react';
import { Text } from '@/shared/ui';

export const inputStyle: CSSProperties = {
  width: '100%',
  height: 40,
  padding: '9px 12px',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface-tertiary)',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'all 0.15s ease',
};

export const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
  display: 'block',
  fontFamily: 'var(--font-family)',
};

export const errorStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--status-error)',
  marginTop: 4,
};

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  suffix?: string;
}

/**
 * Renders a labeled form field wrapper with optional error and suffix.
 * @returns The field wrapper element.
 */
export function Field({ label, error, required, children, suffix }: FieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Text as="label" style={labelStyle}>
        {label}
        {required && <Text as="span" style={{ color: 'var(--status-error)', marginLeft: 2 }}>*</Text>}
      </Text>
      {suffix ? (
        <div style={{ position: 'relative' }}>
          {children}
          <Text as="span" style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'var(--font-family)', pointerEvents: 'none',
          }}>
            {suffix}
          </Text>
        </div>
      ) : children}
      {error && <Text as="span" style={errorStyle}>{error}</Text>}
    </div>
  );
}
